import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { StarRating } from '@/components/StarRating';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/use-toast';
import { CoverImage } from '@/components/CoverImage';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { ArrowLeft, ArrowRight, Star, Search, Loader2, Check, Sparkles, Plus, BookOpen } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { Book } from '@/lib/types';
import { GENRE_OPTIONS, FICTION_GENRES, NONFICTION_GENRES } from '@/lib/constants';
import { OnboardingErrorCard } from '@/components/OnboardingErrorCard';
import { saveUserGenres, saveBookRatings, markGenresComplete, markGenresSkipped, POPULAR_BOOKS as ONBOARDING_POPULAR_BOOKS } from '@/lib/onboarding';
import { PairwiseSheet } from '@/components/PairwiseSheet';

const GENRES = GENRE_OPTIONS;
const FICTION = FICTION_GENRES;
const NONFICTION = NONFICTION_GENRES;
const REQUIRED_GENRES_MIN = 3;
const BOOKS_PER_PAGE = 16;

// Expanded popular books with more options
const POPULAR_BOOKS: Record<string, Array<{ title: string; author: string }>> = {
  'Fiction (General / Contemporary)': [
    { title: 'The Night Circus', author: 'Erin Morgenstern' },
    { title: 'Normal People', author: 'Sally Rooney' },
    { title: 'The Goldfinch', author: 'Donna Tartt' },
    { title: 'Where the Crawdads Sing', author: 'Delia Owens' },
    { title: 'The Vanishing Half', author: 'Brit Bennett' },
    { title: 'Educated', author: 'Tara Westover' },
    { title: 'The Seven Husbands of Evelyn Hugo', author: 'Taylor Jenkins Reid' },
    { title: 'Little Fires Everywhere', author: 'Celeste Ng' },
    { title: 'The Handmaid\'s Tale', author: 'Margaret Atwood' },
    { title: 'Klara and the Sun', author: 'Kazuo Ishiguro' },
    { title: 'The Silent Patient', author: 'Alex Michaelides' },
    { title: 'Gone Girl', author: 'Gillian Flynn' },
    { title: 'The Great Gatsby', author: 'F. Scott Fitzgerald' },
    { title: 'To Kill a Mockingbird', author: 'Harper Lee' },
    { title: '1984', author: 'George Orwell' },
    { title: 'The Kite Runner', author: 'Khaled Hosseini' }
  ],
  'Romance': [
    { title: 'Beach Read', author: 'Emily Henry' },
    { title: 'People We Meet on Vacation', author: 'Emily Henry' },
    { title: 'The Love Hypothesis', author: 'Ali Hazelwood' },
    { title: 'The Hating Game', author: 'Sally Thorne' },
    { title: 'The Kiss Quotient', author: 'Helen Hoang' },
    { title: 'Red, White & Royal Blue', author: 'Casey McQuiston' },
    { title: 'The Spanish Love Deception', author: 'Elena Armas' },
    { title: 'It Ends with Us', author: 'Colleen Hoover' },
    { title: 'Book Lovers', author: 'Emily Henry' },
    { title: 'The Time Traveler\'s Wife', author: 'Audrey Niffenegger' },
    { title: 'Me Before You', author: 'Jojo Moyes' },
    { title: 'The Proposal', author: 'Jasmine Guillory' },
    { title: 'Eleanor Oliphant Is Completely Fine', author: 'Gail Honeyman' },
    { title: 'The Flatshare', author: 'Beth O\'Leary' },
    { title: 'The Unhoneymooners', author: 'Christina Lauren' },
    { title: 'Pride and Prejudice', author: 'Jane Austen' }
  ],
  'Fantasy': [
    { title: 'Harry Potter and the Philosopher\'s Stone', author: 'J.K. Rowling' },
    { title: 'The Hobbit', author: 'J.R.R. Tolkien' },
    { title: 'A Game of Thrones', author: 'George R.R. Martin' },
    { title: 'The Name of the Wind', author: 'Patrick Rothfuss' },
    { title: 'The Way of Kings', author: 'Brandon Sanderson' },
    { title: 'The Final Empire', author: 'Brandon Sanderson' },
    { title: 'The Eye of the World', author: 'Robert Jordan' },
    { title: 'The Lies of Locke Lamora', author: 'Scott Lynch' },
    { title: 'The Blade Itself', author: 'Joe Abercrombie' },
    { title: 'The Shadow of What Was Lost', author: 'James Islington' },
    { title: 'The Poppy War', author: 'R.F. Kuang' },
    { title: 'The Priory of the Orange Tree', author: 'Samantha Shannon' },
    { title: 'Six of Crows', author: 'Leigh Bardugo' },
    { title: 'The Bear and the Nightingale', author: 'Katherine Arden' },
    { title: 'The Fifth Season', author: 'N.K. Jemisin' },
    { title: 'Circe', author: 'Madeline Miller' }
  ],
  'Mystery & Thriller': [
    { title: 'The Girl with the Dragon Tattoo', author: 'Stieg Larsson' },
    { title: 'Gone Girl', author: 'Gillian Flynn' },
    { title: 'The Silent Patient', author: 'Alex Michaelides' },
    { title: 'Big Little Lies', author: 'Liane Moriarty' },
    { title: 'The Woman in the Window', author: 'A.J. Finn' },
    { title: 'In the Woods', author: 'Tana French' },
    { title: 'The Thursday Murder Club', author: 'Richard Osman' },
    { title: 'The Seven Deaths of Evelyn Hardcastle', author: 'Stuart Turton' },
    { title: 'The Guest List', author: 'Lucy Foley' },
    { title: 'Sharp Objects', author: 'Gillian Flynn' },
    { title: 'The Kind Worth Killing', author: 'Peter Swanson' },
    { title: 'Dark Places', author: 'Gillian Flynn' },
    { title: 'The Cuckoo\'s Calling', author: 'Robert Galbraith' },
    { title: 'Rebecca', author: 'Daphne du Maurier' },
    { title: 'And Then There Were None', author: 'Agatha Christie' },
    { title: 'The Murder of Roger Ackroyd', author: 'Agatha Christie' }
  ]
};

interface GenreBooks {
  [genre: string]: {
    books: Book[];
    page: number;
    hasMore: boolean;
    loading: boolean;
  };
}

function OnboardingGenres() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const showDebug = searchParams.get('debug') === '1';
  
  const [selectedGenres, setSelectedGenres] = useState<string[]>([]);
  const [genreBooks, setGenreBooks] = useState<GenreBooks>({});
  const [bookRatings, setBookRatings] = useState<Map<string, number>>(new Map());
  const [saving, setSaving] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [error, setError] = useState<{title: string, message: string, code?: string, tip?: string} | null>(null);
  
  // Quick search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Book[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchType, setSearchType] = useState<'title' | 'author'>('title');
  
  // Pairwise ranking state
  const [showPairwise, setShowPairwise] = useState(false);
  const [pairwiseData, setPairwiseData] = useState<any>(null);
  const [currentRatingBook, setCurrentRatingBook] = useState<Book | null>(null);

  // Auth guard
  useEffect(() => {
    if (!user) {
      navigate('/auth');
      return;
    }
    setAuthLoading(false);
  }, [user, navigate]);

  // Load existing user ratings
  useEffect(() => {
    if (!user) return;
    
    const loadExistingRatings = async () => {
      try {
        const { data: userBooks } = await supabase
          .from('user_books')
          .select('book_id, rating_star')
          .eq('user_id', user.id)
          .not('rating_star', 'is', null);

        if (userBooks) {
          const ratingsMap = new Map();
          userBooks.forEach(ub => {
            if (ub.rating_star) {
              ratingsMap.set(ub.book_id, ub.rating_star);
            }
          });
          setBookRatings(ratingsMap);
        }
      } catch (error) {
        console.error('Error loading existing ratings:', error);
      }
    };

    loadExistingRatings();
  }, [user]);

  // Load existing user genres
  useEffect(() => {
    if (!user) return;
    
    const loadExistingGenres = async () => {
      try {
        const { data: userGenres } = await supabase
          .from('user_genres')
          .select('genre')
          .eq('user_id', user.id);

        if (userGenres) {
          setSelectedGenres(userGenres.map(ug => ug.genre));
        }
      } catch (error) {
        console.error('Error loading existing genres:', error);
      }
    };

    loadExistingGenres();
  }, [user]);

  const fetchBooksForGenre = useCallback(async (genre: string, page: number = 0) => {
    const genreBookList = POPULAR_BOOKS[genre] || [];
    const startIndex = page * BOOKS_PER_PAGE;
    const endIndex = startIndex + BOOKS_PER_PAGE;
    const booksToFetch = genreBookList.slice(startIndex, endIndex);
    
    if (booksToFetch.length === 0) {
      return { books: [], hasMore: false };
    }

    setGenreBooks(prev => ({
      ...prev,
      [genre]: {
        books: prev[genre]?.books || [],
        page: prev[genre]?.page ?? page,
        hasMore: prev[genre]?.hasMore ?? true,
        loading: true
      }
    }));

    try {
      const books: Book[] = [];
      
      for (const bookData of booksToFetch) {
        // Try to find existing book
        const { data: existingBooks } = await supabase
          .from('books')
          .select('*')
          .ilike('title', bookData.title)
          .limit(1);

        if (existingBooks && existingBooks.length > 0) {
          books.push(existingBooks[0]);
        } else {
          // Create new book record
          const { data: newBook } = await supabase
            .from('books')
            .insert({
              title: bookData.title,
              authors: [bookData.author],
              source: 'seed'
            })
            .select()
            .single();

          if (newBook) {
            books.push(newBook);
          }
        }
      }

      const hasMore = endIndex < genreBookList.length;
      
      return { books, hasMore };
    } catch (error) {
      console.error(`Error fetching books for genre ${genre}:`, error);
      throw error;
    }
  }, []);

  const loadGenreBooks = useCallback(async (genre: string, page: number = 0) => {
    try {
      const { books, hasMore } = await fetchBooksForGenre(genre, page);
      
      setGenreBooks(prev => ({
        ...prev,
        [genre]: {
          books: page === 0 ? books : [...(prev[genre]?.books || []), ...books],
          page: page,
          hasMore,
          loading: false
        }
      }));
    } catch (error) {
      setGenreBooks(prev => ({
        ...prev,
        [genre]: {
          books: prev[genre]?.books || [],
          page: prev[genre]?.page || 0,
          hasMore: false,
          loading: false
        }
      }));
      
      setError({
        title: "Error Loading Books",
        message: `Failed to load books for ${genre}.`,
        code: (error as any)?.code || 'BOOKS_FETCH_ERROR',
        tip: "Try refreshing the page or selecting different genres."
      });
    }
  }, [fetchBooksForGenre]);

  // Load books for selected genres
  useEffect(() => {
    selectedGenres.forEach(genre => {
      if (!genreBooks[genre]) {
        loadGenreBooks(genre, 0);
      }
    });
  }, [selectedGenres, genreBooks, loadGenreBooks]);

  // Quick Search with debounce
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }

    const timeoutId = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const column = searchType === 'author' ? 'authors' : 'title';
        const query = supabase
          .from('books')
          .select('*')
          .limit(10);

        if (searchType === 'author') {
          query.contains('authors', [searchQuery]);
        } else {
          query.ilike('title', `%${searchQuery}%`);
        }

        const { data: books } = await query;
        setSearchResults(books || []);
      } catch (error) {
        console.error('Search error:', error);
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 400);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, searchType]);

  // Get all visible books (deduplicated)
  const allVisibleBooks = useMemo(() => {
    const bookMap = new Map<string, Book>();
    
    // Add genre books
    Object.values(genreBooks).forEach(({ books }) => {
      books?.forEach(book => bookMap.set(book.id, book));
    });
    
    // Add search results
    searchResults.forEach(book => bookMap.set(book.id, book));
    
    return Array.from(bookMap.values());
  }, [genreBooks, searchResults]);

  if (!user || authLoading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <span className="ml-2 text-lg">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  const handleGenreToggle = (genre: string) => {
    setSelectedGenres(prev => 
      prev.includes(genre) 
        ? prev.filter(g => g !== genre)
        : [...prev, genre]
    );
  };

  const handleRatingChange = async (bookId: string, rating: number) => {
    setBookRatings(prev => new Map(prev.set(bookId, rating)));
    
    // Optimistically save to database
    if (user && rating > 0) {
      try {
        await supabase
          .from('user_books')
          .upsert({
            user_id: user.id,
            book_id: bookId,
            rating_star: rating,
            preference_score: rating / 5,
            privacy: 'private' // Changed to private default
          }, { onConflict: 'user_id,book_id' });
        
        // Trigger pairwise comparison immediately
        const { data, error } = await supabase.functions.invoke('on-rating-saved', {
          body: {
            userId: user.id,
            bookId,
            rating
          }
        });

        if (!error && data?.needsComparison && data?.comparison) {
          // Find the book details to pass to PairwiseSheet
          const bookDetails = Object.values(genreBooks)
            .flatMap(gb => gb.books)
            .find(b => b.id === bookId) || 
            searchResults.find(b => b.id === bookId);
          
          if (bookDetails) {
            setCurrentRatingBook(bookDetails);
            setPairwiseData(data);
            setShowPairwise(true);
          }
        }
      } catch (error) {
        console.error('Error saving rating:', error);
      }
    }
  };

  const handleLoadMore = (genre: string) => {
    const currentPage = genreBooks[genre]?.page || 0;
    loadGenreBooks(genre, currentPage + 1);
  };

  const handleSave = async () => {
    if (saving) return;
    
    setSaving(true);
    try {
      if (!user) throw new Error('Not authenticated');

      // Save selected genres
      await saveUserGenres(user.id, selectedGenres);

      // Save book ratings
      const ratingsArray = Array.from(bookRatings.entries()).map(([bookId, rating]) => ({
        bookId,
        rating
      }));
      await saveBookRatings(user.id, ratingsArray);

      const ratedCount = Array.from(bookRatings.values()).filter(rating => rating > 0).length;

      toast({
        title: "Genres saved!",
        description: `${selectedGenres.length} genres and ${ratedCount} ratings saved`
      });

      // Mark genres as complete
      await markGenresComplete(user.id);

      // Navigate to archetype quiz
      navigate('/onboarding/archetype', { replace: true });
    } catch (error) {
      console.error('Error saving preferences:', error);
      toast({
        title: "Error saving preferences",
        description: error instanceof Error ? error.message : "Failed to save your preferences. Please try again.",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSkip = async () => {
    if (!user) return;

    try {
      setSaving(true);
      await markGenresSkipped(user.id);
      
      toast({
        title: "Skipped",
        description: "You can always add genres later in your profile settings"
      });

      navigate('/onboarding/archetype', { replace: true });
    } catch (error) {
      console.error('Error skipping genres:', error);
      toast({
        title: "Error",
        description: "Failed to skip. Please try again.",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const BookCard = ({ book }: { book: Book }) => (
    <div className="space-y-2">
      <CoverImage
        bookId={book.id}
        isbn13={book.isbn13}
        isbn10={book.isbn10}
        olid={book.open_library_id}
        title={book.title}
        author={book.authors?.[0]}
        size="sm"
        className="w-full"
      />
      <div className="space-y-1">
        <h4 className="text-xs font-medium leading-tight line-clamp-2">{book.title}</h4>
        <p className="text-[11px] text-muted-foreground line-clamp-1">
          {book.authors?.join(', ')}
        </p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Breadcrumb Navigation */}
      <div className="border-b border-border">
        <div className="container mx-auto px-4 py-4">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Profile
          </Button>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Error Display */}
        {error && (
          <OnboardingErrorCard
            title={error.title}
            message={error.message}
            code={error.code}
            tip={error.tip}
            onRetry={() => {
              setError(null);
              // Retry loading books for selected genres
              selectedGenres.forEach(genre => loadGenreBooks(genre, 0));
            }}
            onBack={() => navigate('/onboarding')}
            showDebugInfo={showDebug}
            debugInfo={{
              selectedGenres,
              bookCount: allVisibleBooks.length,
              errorDetails: error
            }}
          />
        )}

        {/* Main Content */}
        {!error && (
          <>
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold mb-2">Pick your favourite genres</h1>
              <p className="text-muted-foreground mb-4">
                Select at least {REQUIRED_GENRES_MIN} genres you love, then rate some popular books.
              </p>
              <div className="flex items-center justify-center gap-2 p-3 rounded-lg border bg-card max-w-md mx-auto">
                <Sparkles className="w-5 h-5 text-accent flex-shrink-0" />
                <p className="text-sm font-medium">Find your next favourite read, tailored to you</p>
              </div>
            </div>

            {/* Quick Search */}
            <div className="mb-8">
              <div className="relative">
                <div className="flex gap-2 mb-4">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                    <Input
                      placeholder="Quick search any book to rate..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  <div className="flex border rounded-md">
                    <Button
                      variant={searchType === 'title' ? 'default' : 'ghost'}
                      size="sm"
                      onClick={() => setSearchType('title')}
                      className="rounded-r-none"
                    >
                      Title
                    </Button>
                    <Button
                      variant={searchType === 'author' ? 'default' : 'ghost'}
                      size="sm"
                      onClick={() => setSearchType('author')}
                      className="rounded-l-none"
                    >
                      Author
                    </Button>
                  </div>
                </div>

                {/* Search Results */}
                {searchQuery.trim() && (
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold mb-3">Search Results</h3>
                    {searchLoading ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {Array.from({ length: 4 }).map((_, i) => (
                          <Card key={i}>
                            <CardContent className="p-4">
                              <Skeleton className="h-4 w-full mb-2" />
                              <Skeleton className="h-3 w-2/3 mb-3" />
                              <Skeleton className="h-6 w-full" />
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    ) : searchResults.length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {searchResults.map((book) => (
                          <BookCard key={book.id} book={book} />
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted-foreground text-center py-4">
                        No books found for "{searchQuery}". Try a different search term.
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Genres Grid */}
            <div className="mb-8">
              <h2 className="text-xl font-semibold mb-4">
                Your favourite genres ({selectedGenres.length}/{REQUIRED_GENRES_MIN} minimum)
              </h2>
              
              {/* Fiction Section */}
              <div className="mb-6">
                <h3 className="text-base font-medium text-muted-foreground mb-3 flex items-center gap-2">
                  <BookOpen className="w-4 h-4" />
                  Fiction
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {FICTION.map((genre) => (
                    <button
                      key={genre}
                      onClick={() => handleGenreToggle(genre)}
                      className={cn(
                        "p-3 rounded-lg border-2 text-sm font-medium transition-all text-left",
                        "hover:border-primary/50 hover:bg-primary/5",
                        selectedGenres.includes(genre)
                          ? "border-primary bg-primary/10 text-primary"
                          : "border-border bg-card text-card-foreground"
                      )}
                    >
                      <div className="flex items-center justify-between">
                        <span>{genre}</span>
                        {selectedGenres.includes(genre) && (
                          <Check className="h-4 w-4 ml-2 flex-shrink-0" />
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Non-Fiction Section */}
              <div>
                <h3 className="text-base font-medium text-muted-foreground mb-3 flex items-center gap-2">
                  <BookOpen className="w-4 h-4" />
                  Non-Fiction
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {NONFICTION.map((genre) => (
                    <button
                      key={genre}
                      onClick={() => handleGenreToggle(genre)}
                      className={cn(
                        "p-3 rounded-lg border-2 text-sm font-medium transition-all text-left",
                        "hover:border-primary/50 hover:bg-primary/5",
                        selectedGenres.includes(genre)
                          ? "border-primary bg-primary/10 text-primary"
                          : "border-border bg-card text-card-foreground"
                      )}
                    >
                      <div className="flex items-center justify-between">
                        <span>{genre}</span>
                        {selectedGenres.includes(genre) && (
                          <Check className="h-4 w-4 ml-2 flex-shrink-0" />
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Genre Books Sections */}
            {selectedGenres.map((genre) => {
              const genreData = genreBooks[genre];
              if (!genreData) return null;

              return (
                <div key={genre} className="mb-8">
                  <h3 className="text-lg font-semibold mb-4">Popular {genre} Books</h3>
                  
                  <div className="grid grid-cols-2 xs:grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3 sm:gap-4 mb-4">
                    {genreData.books?.map((book) => (
                      <div key={book.id} className="flex flex-col space-y-2">
                        <CoverImage
                          bookId={book.id}
                          isbn13={book.isbn13}
                          isbn10={book.isbn10}
                          olid={book.open_library_id}
                          title={book.title}
                          author={book.authors?.[0]}
                          size="md"
                          className="w-full"
                        />
                        <div className="space-y-0.5 min-h-[3.5rem]">
                          <h4 className="text-sm font-medium leading-snug line-clamp-2 tracking-tight" title={book.title}>
                            {book.title}
                          </h4>
                          <p className="text-xs text-muted-foreground leading-snug line-clamp-1" title={book.authors?.join(', ') || 'Unknown author'}>
                            {book.authors?.join(', ') || 'Unknown author'}
                          </p>
                        </div>
                        <div className="flex justify-center w-full overflow-hidden">
                          <StarRating
                            value={bookRatings.get(book.id) || 0}
                            onChange={(rating) => handleRatingChange(book.id, rating)}
                            size="xs"
                            showLabel={false}
                          />
                        </div>
                      </div>
                    ))}
                  </div>

                  {genreData.loading && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                      {Array.from({ length: 6 }).map((_, i) => (
                        <Card key={i}>
                          <CardContent className="p-4">
                            <Skeleton className="h-4 w-full mb-2" />
                            <Skeleton className="h-3 w-2/3 mb-3" />
                            <Skeleton className="h-6 w-full" />
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}

                  {genreData.hasMore && !genreData.loading && (
                    <div className="text-center">
                      <Button
                        variant="outline"
                        onClick={() => handleLoadMore(genre)}
                        className="flex items-center gap-2"
                      >
                        <Plus className="w-4 h-4" />
                        Load More {genre} Books
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}

            {/* Save and Continue */}
            <div className="flex gap-4 justify-center mt-8">
              <Button
                variant="outline"
                onClick={handleSkip}
                disabled={saving}
              >
                Skip for Now
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving || selectedGenres.length < REQUIRED_GENRES_MIN}
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  `Finish Setup ${selectedGenres.length < REQUIRED_GENRES_MIN ? `(${REQUIRED_GENRES_MIN - selectedGenres.length} more)` : ''}`
                )}
              </Button>
            </div>
          </>
        )}
      </div>
      
      {/* Pairwise comparison sheet */}
      {showPairwise && pairwiseData && currentRatingBook && (
        <PairwiseSheet
          isOpen={showPairwise}
          onClose={() => {
            setShowPairwise(false);
            setPairwiseData(null);
            setCurrentRatingBook(null);
          }}
          currentBook={{
            id: currentRatingBook.id,
            title: currentRatingBook.title,
            authors: currentRatingBook.authors || ['Unknown'],
            cover_url: currentRatingBook.cover_url
          }}
          tier={pairwiseData.tier}
          candidates={[]}
          onComplete={() => {
            setShowPairwise(false);
            setPairwiseData(null);
            setCurrentRatingBook(null);
          }}
          onSkip={() => {
            setShowPairwise(false);
            setPairwiseData(null);
            setCurrentRatingBook(null);
          }}
          placementState={pairwiseData.placementState}
          comparison={pairwiseData.comparison}
        />
      )}
    </div>
  );
}

export default OnboardingGenres;